import { FetchError, ofetch } from 'ofetch';
import type { FetchContext, FetchOptions } from 'ofetch';
import type { z } from 'zod';
import { HttpStatus } from '~/shared/enums/HttpStatus';
import {
  BadRequestApiException,
  ConflictApiException,
  ForbiddenApiException,
  InternalServerErrorException,
  NotFoundApiException,
  UnauthorizedApiException,
  UnhandledApiErrorException,
  UnknownApiErrorException,
} from '~/shared/errors/ApiException';
import type { ApiExceptionConstructorArguments } from '~/shared/errors/ApiException';
import { useAuthStore } from '~/modules/auth/stores/auth.store';

let http: ReturnType<typeof ofetch.create> | null = null;

function useHttp() {
  // Ленивая инициализация: useRuntimeConfig недоступен на этапе импорта модуля.
  // credentials: 'include' обязателен — сессия живёт в httpOnly-куке, а api
  // расположен на другом origin, и без этого браузер куку не приложит
  http ??= ofetch.create({
    baseURL: useRuntimeConfig().public.app.apiUrl,
    credentials: 'include',
    onRequest: onRequestHook,
    onResponseError: onResponseErrorHook,
  });

  return http;
}

function onRequestHook({ options }: FetchContext) {
  const headers = options.headers as Headers;

  if (!headers.has('timeZoneOffset')) {
    headers.set('timeZoneOffset', String(new Date().getTimezoneOffset()));
  }
}

/** Ошибку не гасит: стор уводит на страницу авторизации, а 401 доезжает до вызывающего кода. */
function onResponseErrorHook({ response }: FetchContext) {
  if (response?.status !== HttpStatus.Unauthorized) return;

  void useAuthStore().handleSessionLost();
}

export interface ApiFetchOptions<S extends z.ZodTypeAny> extends FetchOptions<'json'> {
  /** Zod-схема ответа. Обязательна для всех ручек, возвращающих тело. */
  schema?: S;
}

/**
 * Выполняет запрос к API и разбирает ответ по Zod-схеме.
 *
 * @throws {ApiException}
 */
export async function apiFetch<S extends z.ZodTypeAny = z.ZodUndefined>(
  url: string,
  { schema, ...options }: ApiFetchOptions<S> = {},
): Promise<z.output<S>> {
  try {
    const data = await useHttp()(url, options);

    return schema ? schema.parse(data) : data;
  } catch (error) {
    throw mapApiError(error);
  }
}

export function mapApiError(error: unknown): Error {
  // Перехватываем только ошибки API
  if (!(error instanceof FetchError)) {
    return error as Error;
  }

  if (!error.response) {
    return new UnknownApiErrorException();
  }

  const statusCode = error.response.status;
  const body = error.data;

  const args: ApiExceptionConstructorArguments = {
    apiErrorCode: body?.data?.errorCode,
    requestId: body?.requestId,
    method: error.options?.method,
    apiUrl: error.request?.toString(),
  };

  switch (statusCode) {
    case HttpStatus.BadRequest:
      return new BadRequestApiException(args);
    case HttpStatus.Unauthorized:
      return new UnauthorizedApiException(args);
    case HttpStatus.Forbidden:
      return new ForbiddenApiException(args);
    case HttpStatus.NotFound:
      return new NotFoundApiException(args);
    case HttpStatus.Conflict:
      return new ConflictApiException(args);
    default:
      if (statusCode >= HttpStatus.InternalServerError) {
        return new InternalServerErrorException(statusCode, args);
      }

      return new UnhandledApiErrorException(statusCode, args);
  }
}
